import { camelCase } from 'lodash'

import { type FormattedIlfiAttack } from 'components/attacks/utils/kubernetes'
import { cleanObjKeysAndValues } from 'utils/obj'

import type { InfraAttack } from 'types/api/attacks/details/infra-attack'
import type {
  AttackCommandConfiguration,
  AttackConfiguration,
  AttackConfigurationTargetType,
  AttackSamplingConfiguration,
  ContainerTargetingStrategy,
  HostTargetingStrategy,
  KubernetesTargetingStrategy,
  SnakeCaseAttackConfiguration,
  TargetingStrategy
} from 'types/api/attacks/details/targeting'
import type { K8sAttack } from 'types/api/kubernetes/attacks'
import type { InfraAttackNode } from 'types/api/scenarios/scenario'
import type { AttackSchedule } from 'types/api/schedules/active'

export type UnknownAttackVariant =
  | InfraAttack
  | K8sAttack
  | AttackSchedule
  | InfraAttackNode
  | FormattedIlfiAttack

export function isK8sAttack(attack: UnknownAttackVariant): attack is K8sAttack {
  return attack?.hasOwnProperty('uid')
}

export function getAttackConfiguration(attack: UnknownAttackVariant) {
  return isK8sAttack(attack)
    ? attack.attackConfiguration
    : cleanObjKeysAndValues<SnakeCaseAttackConfiguration, AttackConfiguration>(
        attack?.attack_configuration,
        {
          keyTransformers: [camelCase],
          recursive: true
        }
      )
}

export function getCommand(
  attackConfiguration: AttackConfiguration
): AttackCommandConfiguration {
  return attackConfiguration.command
}

export function getTargetingStrategy(
  attackConfiguration?: AttackConfiguration,
  index = 0
): TargetingStrategy {
  return (
    attackConfiguration?.targets?.[index]?.targetingStrategy ??
    ({} as TargetingStrategy)
  )
}

export function getTargetType(
  attackConfiguration: AttackConfiguration
): AttackConfigurationTargetType {
  return getTargetingStrategy(attackConfiguration).type
}

export function isHostTargetType(
  targetingStrategy?: TargetingStrategy
): targetingStrategy is HostTargetingStrategy {
  return targetingStrategy?.type === 'Host'
}

export function isContainerTargetType(
  targetingStrategy?: TargetingStrategy
): targetingStrategy is ContainerTargetingStrategy {
  return targetingStrategy?.type === 'Container'
}

export function isK8sTargetType(
  targetingStrategy?: TargetingStrategy
): targetingStrategy is KubernetesTargetingStrategy {
  return targetingStrategy?.type === 'Kubernetes'
}

export function getSampling(
  attackConfiguration: AttackConfiguration
): AttackSamplingConfiguration {
  return attackConfiguration.sampling
}

export function getServiceIdFromAttack(attack: UnknownAttackVariant) {
  if ('service_id' in attack) {
    return attack.service_id
  }
  if ('serviceId' in attack) {
    return attack.serviceId
  }

  const attackConfig = getAttackConfiguration(attack)

  const firstTarget = attackConfig?.targets?.[0]

  return firstTarget?.serviceId ?? firstTarget?.service_id
}
